import { Component, ViewChild, AfterViewInit, TemplateRef, input, effect, output, OutputRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GenericFilterComponent } from '../generic-filter/generic-filter.component';
import { UtilService } from '@services/util.service';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';
import { SelectionModel } from '@angular/cdk/collections';
import { ColumnType, FilterConfig } from '@models/shared.models';

export interface TableConfig {
  enablePagination?: boolean;
  enableSort?: boolean;
  enableSearch?: boolean;  // Changed from enableFilter
  enableFilter?: boolean;  // Changed from showFilterButton
  enableCSVDownload?: boolean;
  enableExcelDownload?: boolean;
  showSerialNumber?: boolean;
  stickyHeader?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  enableRowSelection?: boolean;
  multipleSelection?: boolean;
  allowedNested?: boolean;
}

export interface TableColumn {
  key: string;
  header: string;
  sortable?: boolean;
  type?: ColumnType;
  filterable?: boolean;
  template?: TemplateRef<any>;
  sticky?: boolean;
}

@Component({
  selector: 'app-generic-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    JsonViewerComponent,
    MatCheckboxModule,
    DatePipe
  ],
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss']
})
export class GenericTableComponent implements AfterViewInit {
  data = input<any[]>([]);
  columns = input<TableColumn[]>([]);
  tableClasses = input<string>('');
  config = input<TableConfig>({
    enablePagination: false,
    enableSort: false,
    enableSearch: false,
    enableFilter: false,
    enableCSVDownload: false,
    enableExcelDownload: false,
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 100],
    enableRowSelection: false,
    multipleSelection: false
  });

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<any>;

  displayedColumns: string[] = [];
  dataSource!: MatTableDataSource<any>;
  showFilter = false;
  filterableColumns: { name: string; type: ColumnType; }[] = [];
  activeFilterConfig: FilterConfig | null = null;
  searchText: string = '';
  selectedRows = output<any[]>();
  selection: SelectionModel<any>;

  constructor(private dialog: MatDialog, private utilService: UtilService) {
    this.dataSource = new MatTableDataSource(this.data());
    this.selection = new SelectionModel<any>(this.config().multipleSelection || false, []);

    // Subscribe to input signal changes
    effect(() => {
      const newData = this.data();
      if (this.dataSource) {
        this.dataSource.data = newData;
        this.applyFilters();
      }
    });

    effect(() => {
      // This effect depends on both columns and config signals
      const newColumns = this.columns();
      const newConfig = this.config();

      this.selection = new SelectionModel<any>(newConfig.multipleSelection || false, []);
      
      // Update displayed columns based on both signals
      this.displayedColumns = newConfig.showSerialNumber
        ? ['serialNumber', ...newColumns.map(col => col.key)]
        : newColumns.map(col => col.key);
        
      // Add selection column if needed
      if (newConfig.enableRowSelection && this.displayedColumns[0] !== 'select') {
        this.displayedColumns = ['select', ...this.displayedColumns];
      } else if (!newConfig.enableRowSelection) {
        this.displayedColumns = this.displayedColumns.filter(col => col !== 'select');
      }
      
      // Update filterable columns
      this.filterableColumns = newColumns
        .filter(col => col.filterable !== false)
        .map(col => ({
          name: col.key,
          type: col.type || ColumnType.String
        }));
        
      this.setupFilterPredicate();
    });

    // Emit selection changes
    // effect(() => {
    //   this.selectedRows.emit(this.selection.selected);
    // });
  }

  ngAfterViewInit() {
    if (this.config().enablePagination) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.config().enableSort) {
      this.dataSource.sort = this.sort;
    }
  }

  onRowSelected(row: any) {
    this.selection.toggle(row);
    this.selectedRows.emit(this.selection.selected);
  }

  toggleFilter() {
    interface FilterDialogData {
      columns: { name: string; displayName: string; type: ColumnType; }[];
      activeFilter: FilterConfig | null;
    }

    // Create columns with both key and display name
    const filterColumns = this.filterableColumns.map(col => {
      const columnDef = this.columns().find(c => c.key === col.name);
      return {
        name: col.name,
        displayName: columnDef?.header || col.name,
        type: col.type
      };
    });

    this.utilService.openPopupCustom<FilterConfig, FilterDialogData>(
      GenericFilterComponent, 
      { columns: filterColumns, activeFilter: this.activeFilterConfig },
      '800px'
    ).subscribe(result => {
      if (result) {
        this.activeFilterConfig = result;
        this.onFilterApplied(result);
      }
    });
  }

  private setupFilterPredicate() {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      // Always return true if no filters are active
      if (!this.searchText && !this.activeFilterConfig) {
        return true;
      }

      // Search text filter
      const matchesSearch = !this.searchText || Object.keys(data).some(key => {
        const value = data[key];
        return value !== null && value !== undefined && 
          value.toString().toLowerCase().includes(this.searchText.toLowerCase());
      });

      // Advanced filter
      const matchesFilter = !this.activeFilterConfig || this.evaluateConditions(data, this.activeFilterConfig);

      // Both conditions must be met
      return matchesSearch && matchesFilter;
    };
  }

  private evaluateConditions(data: any, filterConfig: FilterConfig): boolean {
    const conditionResults = filterConfig.conditions.map(condition => {
      const value = data[condition.columnName];
      
      if (condition.columnType === ColumnType.String) {
        const stringValue = String(value).toLowerCase();
        const filterValue = String(condition.value).toLowerCase();
        
        switch (condition.operator) {
          case 'equals':
            return stringValue === filterValue;
          case 'startsWith':
            return stringValue.startsWith(filterValue);
          case 'endsWith':
            return stringValue.endsWith(filterValue);
          case 'contains':
            return stringValue.includes(filterValue);
          default:
            return false;
        }
      } else if (condition.columnType === ColumnType.Number) {
        const numValue = Number(value);
        const filterValue = Number(condition.value);
        
        switch (condition.operator) {
          case 'equals':
            return numValue === filterValue;
          case 'greaterThan':
            return numValue > filterValue;
          case 'lessThan':
            return numValue < filterValue;
          case 'greaterThanOrEqual':
            return numValue >= filterValue;
          case 'lessThanOrEqual':
            return numValue <= filterValue;
          default:
            return false;
        }
      } else if (condition.columnType === ColumnType.Boolean) {
        const booleanValue = Boolean(value);
        
        switch (condition.operator) {
          case 'equals':
            return booleanValue === Boolean(condition.value);
          case 'notEquals':
            return booleanValue !== Boolean(condition.value);
          case 'isTrue':
            return booleanValue === true;
          case 'isFalse':
            return booleanValue === false;
          default:
            return false;
        }
      } else if (condition.columnType === ColumnType.Date) {
        // Convert value to date if it's not already
        const dateValue = value instanceof Date ? value : new Date(value);
        const filterDate = condition.value instanceof Date ? condition.value : new Date(condition.value as string);
        
        // Check if dates are valid
        if (isNaN(dateValue.getTime()) || isNaN(filterDate.getTime())) {
          return false;
        }

        // Reset times to compare dates only (if needed)
        const normalizeDate = (date: Date): Date => {
          const normalized = new Date(date);
          normalized.setHours(0, 0, 0, 0);
          return normalized;
        };

        const normalizedValue = normalizeDate(dateValue);
        const normalizedFilter = normalizeDate(filterDate);

        switch (condition.operator) {
          case 'equals':
            return normalizedValue.getTime() === normalizedFilter.getTime();
          case 'before':
            return normalizedValue < normalizedFilter;
          case 'after':
            return normalizedValue > normalizedFilter;
          case 'between':
            if (!condition.endValue) return false;
            const endDate = condition.endValue instanceof Date ? 
              condition.endValue : new Date(condition.endValue as string);
            if (isNaN(endDate.getTime())) return false;
            const normalizedEnd = normalizeDate(endDate);
            return normalizedValue >= normalizedFilter && normalizedValue <= normalizedEnd;
          default:
            return false;
        }
      }
      return false;
    });

    return filterConfig.operator === 'AND' 
      ? conditionResults.every(result => result)
      : conditionResults.some(result => result);
  }

  onFilterApplied(filterConfig: FilterConfig) {
    this.activeFilterConfig = filterConfig;
    this.applyFilters();
  }

  clearFilter() {
    this.activeFilterConfig = null;
    this.applyFilters();
  }

  applySearch(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.applyFilters();
  }

  private applyFilters() {
    // Trigger the filter
    this.dataSource.filter = 'trigger';

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  downloadAsCSV() {
    //NOT IMPLEMENTED
  }

  downloadAsExcel() {
    //NOT IMPLEMENTED
  }

  /** Checks whether an element is expanded. */
  isExpanded(element: any, column?:any) {
    if(column){
      return this.expandedElement?.element === element && this.expandedElement?.column === column;
    }
    return this.expandedElement?.element === element;
  }

  /** Toggles the expanded state of an element. */
  toggleExpand(element: any, column: string): void {
    console.log(element)
    if (
      this.expandedElement?.element === element &&
      this.expandedElement?.column === column
    ) {
      this.expandedElement = null;
    } else {
      this.expandedElement = { element, column };
    }
  }

  expandedElement:any

  openDialog(element: any, column: string) {
    const dialogRef = this.dialog.open(JsonViewerComponent, {width: '800px'});

    dialogRef.componentInstance.json.set(element?.[column]);
    dialogRef.componentInstance.mode.set('dialog')
    dialogRef.componentInstance.ngOnChanges();
  }

  isExpandable(element: any, column: string, colType?: ColumnType): boolean {
    if (colType === ColumnType.Date) {
      return false;
    }
    return element?.[column] && (typeof element?.[column] === 'object' || Array.isArray(element?.[column]));
  }
  
  /** Returns the index of the data item in the MatTableDataSource */
  dataIndex(element: any): number {
    return this.dataSource.data.indexOf(element);
  }
  /** Whether all rows are selected */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
    this.selectedRows.emit(this.selection.selected);
  }

  /** Whether the number of selected elements matches the total number of rows */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
  }
}